import cx from 'classnames';
import { Navigate, useLocation } from 'react-router-dom';
import { APP_ROUTES } from '../../routes';
import { DataStore } from '../../store';
import { AppFooter } from '../app-footer';
import { AppHeader } from '../app-header';
import { useLeftNav } from '~/hooks/use-left-nav';
import { memo, useCallback, useRef } from 'react';
import StepWizard from 'react-step-wizard';
import { defaultStepWizardProps } from '~/constants';
import { SplashBanner } from '../splash-banner';
import { TwoColumnLayout, useBreakpoints } from '@xbto/universal-components';
import { useFundsExperienceNav } from '~/hooks/use-funds-experience-nav';
import { useAccountSelector } from '~/hooks/use-account-selector';
import { useExperience } from '~/hooks/use-experience';
import { useInlineBanners } from '~/hooks/use-inline-banners';

interface Props {
  children: JSX.Element;
}

export const RequireAuth = memo(({ children }: Props) => {
  /**
   * Store
   */
  const isAuthenticated = DataStore.useStoreState(s => s.user.isAuthenticated);
  const isEmailVerified = DataStore.useStoreState(s => s.user.isEmailVerified);
  const busy = DataStore.useStoreState(s => s.busy);

  const acceptMessage = DataStore.useStoreActions(s => s.alerts.acceptMessage);

  const dismissMessage = DataStore.useStoreActions(
    s => s.alerts.dismissMessage
  );

  const splashScreens = DataStore.useStoreState(s => s.alerts.splashScreens);

  /**
   * Hooks
   */
  const { breakpoints } = useBreakpoints();
  const location = useLocation();
  const handleAccept = useCallback(
    async (userMessageId: string, arg?: () => void) => {
      const result = await acceptMessage({ userMessageId });
      if (result.isSuccessful) {
        arg?.();
      }
    },
    [acceptMessage]
  );

  const handleSplashDismiss = useCallback(
    async (userMessageId: string, arg?: () => void) => {
      const result = await dismissMessage({ userMessageId });
      if (result.isSuccessful) {
        arg?.();
      }
    },
    [dismissMessage]
  );

  /**
   * Hooks
   */
  const showFundsExperience = useExperience();
  const { openByAccountType: openAccountSelector, Selector: AccountSelector } =
    useAccountSelector();
  const { leftNavCls, Nav } = useLeftNav();
  const { Nav: FundsNav } = useFundsExperienceNav(openAccountSelector);
  const ref = useRef<HTMLDivElement | null>(null);
  const inlineBanners = useInlineBanners();

  /**
   * DOM
   */
  if (!busy && isAuthenticated && !isEmailVerified) {
    return (
      <Navigate
        to={{
          pathname: APP_ROUTES.NON_AUTH_EMAIL_VERIFICATION_PENDING,
        }}
        state={{
          from: location,
        }}
      />
    );
  }

  if (!busy && !isAuthenticated) {
    return (
      <Navigate
        to={{
          pathname: APP_ROUTES.NON_AUTH_HOME,
        }}
        state={{
          from: location,
        }}
      />
    );
  }

  if (splashScreens.length) {
    return (
      <StepWizard
        className="flex flex-col h-screen flex-1"
        {...defaultStepWizardProps}
      >
        {splashScreens.map(i => (
          <SplashBanner
            {...i}
            key={i.id}
            onAccept={handleAccept}
            onDismiss={handleSplashDismiss}
          />
        ))}
      </StepWizard>
    );
  }

  const hasBanner =
    location.pathname === APP_ROUTES.AUTH_MARKETS ||
    location.pathname === APP_ROUTES.AUTH_PORTFOLIO_OVERVIEW;

  if (showFundsExperience) {
    const content = (
      <div className="flex-1 flex" ref={ref}>
        <div className={'flex flex-col w-full relative h-full text-primary'}>
          {hasBanner && inlineBanners}
          {children}
          <AppFooter variant="xbto" />
        </div>
      </div>
    );
    return (
      <div className="flex flex-col h-screen flex-1 max-w-full">
        <AccountSelector />
        <TwoColumnLayout
          noLeftCol={false}
          leftContent={breakpoints.MORE_THAN_M && FundsNav}
          rightContent={
            breakpoints.LESS_THAN_M ? (
              <>
                {FundsNav}
                {content}
              </>
            ) : (
              content
            )
          }
        />
      </div>
    );
  }

  return (
    <div className={'flex flex-col h-screen flex-1'}>
      <span className={leftNavCls}>
        <AppHeader />
      </span>
      {hasBanner && inlineBanners}
      <div className="flex-1 flex" ref={ref}>
        {Nav}
        <div
          className={cx(
            'flex flex-col w-full relative h-full text-primary',
            leftNavCls
          )}
        >
          {children}
          <AppFooter />
        </div>
      </div>
    </div>
  );
});
